export type MapEditorBootstrapData = {
  assetBaseUrl?: string;
  useHostBaiduMapSdk?: boolean;
  permissions?: string[];
  mode?: 'micro-app';
  login?: {
    appResourceInfoList?: Array<{ resourceCode?: string }>;
    resourceList?: Array<{ code?: string; resourceCode?: string }>;
    [key: string]: unknown;
  };
  // 宿主直接透传登录信息响应时的资源列表（含 envelope 形态 data.resourceList）
  resourceList?: Array<{ code?: string; resourceCode?: string }>;
  data?: {
    resourceList?: Array<{ code?: string; resourceCode?: string }>;
  };
  [key: string]: unknown;
};

const getHostWindow = () =>
  globalThis as unknown as Window & {
    __MAP_EDITOR_BOOTSTRAP_DATA__?: MapEditorBootstrapData;
    __MAP_EDITOR_ASSET_BASE_URL__?: string;
    __MICRO_APP_PUBLIC_PATH__?: string;
    microApp?: {
      getData?: () => MapEditorBootstrapData | null;
      dispatch?: (data: Record<string, unknown>) => void;
      addDataListener?: (
        callback: (data: MapEditorBootstrapData) => void,
        autoTrigger?: boolean,
      ) => void;
      removeDataListener?: (
        callback: (data: MapEditorBootstrapData) => void,
      ) => void;
    };
  };

const normalizePublicPath = (publicPath?: string) => {
  if (!publicPath) return undefined;
  return /\/$/.test(publicPath) ? publicPath : `${publicPath}/`;
};

/**
 * 读取宿主传给 MapEditor 子应用的启动数据。
 *
 * 长期协议优先使用 MapEditor 自有全局变量，避免依赖 micro-app sandbox 注入。
 * microApp.getData 仅作为兼容旧接入方式的兜底。
 */
export const getMapEditorBootstrapData = (): MapEditorBootstrapData => {
  const win = getHostWindow();
  return win.__MAP_EDITOR_BOOTSTRAP_DATA__ || win.microApp?.getData?.() || {};
};

/** 获取子应用静态资源基准路径，支持 disableSandbox 与 sandbox 两种运行方式。 */
export const getMapEditorAssetBaseUrl = () => {
  const win = getHostWindow();
  const data = getMapEditorBootstrapData();
  return normalizePublicPath(
    data.assetBaseUrl ||
      win.__MAP_EDITOR_ASSET_BASE_URL__ ||
      win.__MICRO_APP_PUBLIC_PATH__,
  );
};

/**
 * 判断资源权限。兼容三种数据形态：
 * 1. 新协议 permissions: string[]
 * 2. 旧协议 login.appResourceInfoList[].resourceCode
 * 3. 宿主直接透传登录信息响应：resourceList[].code（含 envelope 形态 data.resourceList）
 */
export const hasMapEditorPermission = (resourceCode: string) => {
  const data = getMapEditorBootstrapData();

  const resourceLists: unknown[] = [
    data.permissions,
    data.login?.appResourceInfoList,
    data.login?.resourceList,
    data.resourceList,
    data.data?.resourceList,
  ];

  return resourceLists.some(
    (list) =>
      Array.isArray(list) &&
      list.some(
        (item) =>
          item === resourceCode ||
          item?.resourceCode === resourceCode ||
          item?.code === resourceCode,
      ),
  );
};

export const isMapEditorMicroAppMode = () => {
  const data = getMapEditorBootstrapData();
  return data.mode === 'micro-app' || Boolean(getMapEditorAssetBaseUrl());
};
